import React, { useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { FlexRow, TableEditButtons, usePaginatedQueryV8 } from 'motion-components';
import { Card } from 'react-bootstrap';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useUserLocationPermissions } from 'shared-pages/hooks';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { gqlGetAppparams, gqlGetEntityParamGroups, gqlGetEntityParams } from '../graphql';
import { buildWhereFromFilterValues, Filters } from './entity-filters';
import { DataGrid } from './entity-grid';

/* ----------------------------------------------------------- */
/* ------------ Παράμετροι ανά Τύπο Παραλήπτη ---------------- */
export const EntityParamsSection = observer(() => {
  const { appStore } = useRootStore();
  const { handleGraphqlError } = useHandleError('EntityParamsSection');
  // const { canAddNew } = useUserLocationPermissions();
  const canAddNew = false;

  const location = useLocation<any>();
  const history = useHistory();
  const [selected, setSelected] = useState<any>(null);
  const { t } = useTranslation();

  const { data: appparamsData } = useQuery(gqlGetAppparams, {
    variables: {
      where: JSON.stringify({}),
      limit: 1000,
      offset: 0,
      order: 'paramName ASC',
    },
    fetchPolicy: 'cache-and-network',
  });
  const { data: groupsData } = useQuery(gqlGetEntityParamGroups, {
    fetchPolicy: 'cache-and-network',
  });

  /* ------------------- Query ------------------------------- */
  const initOrderBy = 'paramName ASC';
  const query = usePaginatedQueryV8(
    gqlGetEntityParams,
    buildWhereFromFilterValues,
    location.state?.order ?? initOrderBy,
    appStore,
    true,
    { applies: 1 },
    true,
  );
  const {
    loading,
    data,
    dataCount,
    error,
    initPageIndex,
    filtersChanged,
    gridPageChanged,
    queryState,
    queryParams,
    setQueryParams,
    refetch,
  } = query;

  const gridRef = useRef();
  const appParamMap = useMemo(() => {
    const list: any[] = appparamsData?.getAppparams?.data ?? [];
    const map = new Map<string, { description?: string; notes?: string; paramGroup?: string }>();
    list.forEach((item) =>
      map.set(item.paramName, {
        description: item.description ?? '',
        notes: item.notes ?? '',
        paramGroup: item.paramGroup ?? '',
      }),
    );
    return map;
  }, [appparamsData]);

  const groupOptions = useMemo(() => {
    const options = groupsData?.getEntityParamGroups ?? [];
    return [{ value: '', label: t('Όλες οι Ομάδες') }, ...options];
  }, [groupsData, t]);

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  }

  return (
    <Card className="border-0 shadow-none">
      <Card.Header className="bg-transparent border-0">
        <FlexRow>
          <small className="text-muted">
            {t(
              'Ορίζει αν στέλνεται/ισχύει η παράμετρος ανά τύπο παραλήπτη. Παρακάμπτεται από user-level overrides.',
            )}
          </small>
          <TableEditButtons
            className="me-2"
            canEdit={true}
            canInsert={canAddNew}
            canDelete={true}
            tableOptionsMeta={() => {
              if (gridRef.current) return (gridRef.current as any).table.options.meta;
              else return undefined;
            }}
            twoLines={false}
          />
        </FlexRow>
      </Card.Header>
      <Card.Body className="nopadding">
        <Filters
          onChange={filtersChanged}
          initialValues={queryState?.filterValues ?? {}}
          groupOptions={groupOptions}
        />
        <DataGrid
          ref={gridRef}
          loading={loading}
          data={data}
          appParamMap={appParamMap}
          dataCount={dataCount}
          initPageIndex={initPageIndex}
          onPageChange={gridPageChanged}
          initOrderBy={queryParams?.order ?? initOrderBy}
          onOrderChange={(orderBy: string) => {
            const newQueryParams = { ...queryParams, order: orderBy };
            setQueryParams(newQueryParams);
            history.replace({
              pathname: location.pathname,
              state: {
                ...location.state,
                order: orderBy,
              },
            });
          }}
          selected={selected}
          setSelected={setSelected}
          refetch={refetch}
        />
      </Card.Body>
    </Card>
  );
});
